import { CatalogNode } from './catalogDoc';
import { calcCaption } from './calcOp';

export const CATEGORY_COLOR: Record<string, string> = {
  controller: '#004d40',
  bus: '#37474f',
  device: '#1b5e20',
  point: '#2e7d32',
  timer: '#00838f',
  calc: '#ef6c00',
  label: '#1565c0',
  mqtt_topic: '#455a64',
  zigbee_point: '#00695c',
  dashboard: '#6a1b9a',
  threshold: '#c62828',
  notify: '#ad1457',
  toggle: '#4527a0',
};

export type PalettePayload =
  | { kind: 'controller'; fleetId: string; hostname: string; label: string }
  | { kind: 'bus'; ownerId: string; of: string; busType: string; role?: string }
  | { kind: 'device'; ownerId: string; of: string; deviceType: string; connection: string }
  | { kind: 'point'; ownerId: string; of: string; key: string; poll?: boolean }
  | { kind: 'mqtt_topic' }
  | { kind: 'zigbee_point' }
  | { kind: 'timer' }
  | { kind: 'calc' }
  | { kind: 'label'; layoutId: string; widgetId: string; title: string }
  | { kind: 'dashboard'; widgetType: string }
  | { kind: 'threshold' }
  | { kind: 'notify' }
  | { kind: 'toggle' };

export function catalogNodeTitle(
  node: CatalogNode,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  if (node.type === 'controller') {
    return node.hostname || node.id;
  }
  if (node.type === 'bus') {
    const role = node.role ? ` ${node.role}` : '';
    return `${node.busType || 'bus'}${role}${node.of ? ` · ${node.of}` : ''}`;
  }
  if (node.type === 'device') {
    return node.of || node.deviceType || node.id;
  }
  if (node.type === 'point') {
    return node.of && node.key ? `${node.of}.${node.key}` : (node.key || node.id);
  }
  if (node.type === 'timer') {
    if (node.interval_ms) {
      return `${node.interval_ms}ms`;
    }
    return `${node.interval || 60}s`;
  }
  if (node.type === 'calc') {
    return calcCaption(node.op, node.value);
  }
  if (node.type === 'label') {
    return node.widget ? `${node.layout || ''} · ${node.widget}` : t('flow.layoutLabel');
  }
  if (node.type === 'mqtt_topic') {
    return node.topic || t('flow.mqttTopic');
  }
  if (node.type === 'zigbee_point') {
    return node.of && node.key ? `${node.of}.${node.key}` : t('flow.brick.zigbee_point');
  }
  if (node.type === 'dashboard') {
    return node.title || t('flow.dashboardWidget');
  }
  if (node.type === 'threshold') {
    const parts: string[] = [];
    if (node.min != null) {
      parts.push(`≥${node.min}`);
    }
    if (node.max != null) {
      parts.push(`≤${node.max}`);
    }
    if (node.match) {
      parts.push(node.match);
    }
    return node.title || (parts.length ? parts.join(' · ') : t('flow.brick.threshold'));
  }
  if (node.type === 'notify') {
    return node.title || t('flow.brick.notify');
  }
  if (node.type === 'toggle') {
    const edge = node.edge === 'falling' ? t('flow.toggleFalling') : t('flow.toggleRising');
    return node.title ? `${node.title} · ${edge}` : `${t('flow.brick.toggle')} · ${edge}`;
  }
  return node.id;
}

export function palettePreviewNode(
  payload: PalettePayload,
  t: (key: string, vars?: Record<string, string | number>) => string,
): CatalogNode {
  const base = { id: 'preview', x: 0, y: 0 };
  if (payload.kind === 'controller') {
    return { ...base, type: 'controller', hostname: payload.hostname || payload.label };
  }
  if (payload.kind === 'bus') {
    return {
      ...base,
      type: 'bus',
      of: payload.of,
      busType: payload.busType,
      role: payload.role,
    };
  }
  if (payload.kind === 'device') {
    return { ...base, type: 'device', of: payload.of, deviceType: payload.deviceType };
  }
  if (payload.kind === 'point') {
    return {
      ...base,
      type: 'point',
      of: payload.of,
      key: payload.key,
      poll: payload.poll,
    };
  }
  if (payload.kind === 'mqtt_topic') {
    return { ...base, type: 'mqtt_topic', topic: '{base}/' };
  }
  if (payload.kind === 'zigbee_point') {
    return { ...base, type: 'zigbee_point', of: 'inputs', key: 'button' };
  }
  if (payload.kind === 'timer') {
    return { ...base, type: 'timer', interval: 60 };
  }
  if (payload.kind === 'calc') {
    return { ...base, type: 'calc', op: '*', value: 0.1 };
  }
  if (payload.kind === 'label') {
    return { ...base, type: 'label', layout: payload.layoutId, widget: payload.widgetId };
  }
  if (payload.kind === 'threshold') {
    return { ...base, type: 'threshold', min: 0, max: 40, hysteresis: 0.5 };
  }
  if (payload.kind === 'notify') {
    return {
      ...base,
      type: 'notify',
      title: t('flow.brick.notify'),
      criticality: 2,
      channels: ['pushover', 'mail'],
    };
  }
  if (payload.kind === 'toggle') {
    return { ...base, type: 'toggle', edge: 'rising', title: t('flow.brick.toggle') };
  }
  return {
    ...base,
    type: 'dashboard',
    widgetType: payload.widgetType,
    title: payload.widgetType === 'relay'
      ? t('flow.relayWidget')
      : payload.widgetType === 'stacklight'
        ? t('flow.stacklightWidget')
        : t('flow.climateWidget'),
  };
}
